import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Id } from "./types";

export interface BoxBuilderState {
  tierVariationId: Id | null;
  selectedItems: Record<Id, number>;
  selectedPremium: Id[];
  tissueId: Id | null;

  setTier: (variationId: Id | null) => void;
  addItem: (id: Id) => void;
  removeItem: (id: Id) => void;
  setItemQuantity: (id: Id, quantity: number) => void;
  togglePremium: (id: Id) => void;
  setTissue: (id: Id) => void;
  reset: () => void;
}

export const useBoxBuilder = create<BoxBuilderState>()(
  persist(
    (set) => ({
      tierVariationId: null,
      selectedItems: {},
      selectedPremium: [],
      tissueId: null,

      setTier: (variationId) =>
        set((s) =>
          s.tierVariationId === variationId
            ? s
            : {
                ...s,
                tierVariationId: variationId,
                selectedItems: {},
              },
        ),

      addItem: (id) =>
        set((s) => ({
          ...s,
          selectedItems: {
            ...s.selectedItems,
            [id]: (s.selectedItems[id] ?? 0) + 1,
          },
        })),

      removeItem: (id) =>
        set((s) => {
          const next = { ...s.selectedItems };
          const current = next[id] ?? 0;
          if (current <= 1) {
            delete next[id];
          } else {
            next[id] = current - 1;
          }
          return { ...s, selectedItems: next };
        }),

      setItemQuantity: (id, quantity) =>
        set((s) => {
          const next = { ...s.selectedItems };
          if (quantity <= 0) {
            delete next[id];
          } else {
            next[id] = quantity;
          }
          return { ...s, selectedItems: next };
        }),

      togglePremium: (id) =>
        set((s) => ({
          ...s,
          selectedPremium: s.selectedPremium.includes(id)
            ? s.selectedPremium.filter((x) => x !== id)
            : [...s.selectedPremium, id],
        })),

      setTissue: (id) => set((s) => ({ ...s, tissueId: id })),

      reset: () =>
        set({
          tierVariationId: null,
          selectedItems: {},
          selectedPremium: [],
          tissueId: null,
        }),
    }),
    {
      name: "biab-box-builder",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
